import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CONFIG_SECTIONS } from "config/adminConfig";
import { isLiriAdmin, parseJwtPayload } from "util/moduleAccess";
import { getSectionIcon } from "./sectionMeta";
import LiriAdminBuilds from "./components/LiriAdminBuilds";
import LeavePolicy from "./components/LeavePolicy/LeavePolicy";
import LeaveRecipientConfig from "./components/LeaveRecipientConfig";
import { GenericConfigDetail } from "./ConfigDetail";
import { UploadConfigDetail } from "./UploadConfigDetail";

const CONFIG_HOME = "/admin/admin-config";

/**
 * Module settings page (/admin/admin-config/:sectionKey).
 *
 * Renders a module's sub-configurations as tabs with the active one's editor
 * below. Sections that already ship a rich, dedicated UI (Leave Policy, Liri
 * Admin, Leave Recipients) are rendered as-is — they own their headers.
 */
const ModuleConfig: React.FC = () => {
  const { sectionKey } = useParams<{ sectionKey: string }>();
  const navigate = useNavigate();

  // Same permission gate the landing uses, so a direct URL can't reach a
  // restricted module (Liri Admin).
  const token = localStorage.getItem("accessToken");
  const payload = parseJwtPayload(token);
  let userEmail: string | null = null;
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) userEmail = JSON.parse(userStr)?.email ?? null;
  } catch {
    userEmail = null;
  }
  const isLiriAdminUser = isLiriAdmin(payload, undefined, userEmail);

  const section = useMemo(
    () => CONFIG_SECTIONS.find((s) => s.key === sectionKey),
    [sectionKey],
  );

  const [activeFieldKey, setActiveFieldKey] = useState<string | undefined>(
    section?.fields[0]?.key,
  );

  const allowed = !!section && (section.key !== "liriadmin" || isLiriAdminUser);

  if (!allowed || !section) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
        <p className="text-lg font-medium">Configuration not found</p>
        <button
          onClick={() => navigate(CONFIG_HOME)}
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back to configuration
        </button>
      </div>
    );
  }

  // Sections with a dedicated full-page UI (own header/back) render directly,
  // just wrapped so they fill the available height.
  const fullPage = (node: React.ReactNode) => (
    <div className="h-full w-full max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col">
      {node}
    </div>
  );

  if (section.key === "liriadmin") return fullPage(<LiriAdminBuilds />);
  if (section.key === "leavePolicy") return fullPage(<LeavePolicy />);
  if (section.fields.length === 1 && section.fields[0].variant === "recipients") {
    return fullPage(<LeaveRecipientConfig field={section.fields[0]} />);
  }

  const Icon = getSectionIcon(section.key);
  const activeField =
    section.fields.find((f) => f.key === activeFieldKey) ?? section.fields[0];
  const showTabs = section.fields.length > 1;

  return (
    <div className="h-full w-full px-4 sm:px-6 lg:px-8 py-4 flex flex-col text-slate-900 dark:text-white">
      {/* Header — back + breadcrumb + title + description */}
      <header className="flex-shrink-0 mb-4">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => navigate(CONFIG_HOME)}
            aria-label="Back to configuration"
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <nav aria-label="Breadcrumb" className="text-xs text-gray-400 dark:text-gray-500">
            <button
              type="button"
              onClick={() => navigate(CONFIG_HOME)}
              className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              Admin Configuration
            </button>
            <span className="mx-1.5">/</span>
            <span className="text-gray-600 dark:text-gray-300 font-medium">{section.label}</span>
          </nav>
        </div>

        <div className="mt-2.5 flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
            <Icon size={20} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {section.label} settings
            </h1>
            {section.description && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {section.description}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Sub-config tabs (only when there's more than one) */}
      {showTabs && (
        <div
          role="tablist"
          aria-label={`${section.label} configurations`}
          className="flex-shrink-0 flex gap-2 overflow-x-auto pb-3"
        >
          {section.fields.map((f) => {
            const isActive = activeField.key === f.key;
            return (
              <button
                key={f.key}
                role="tab"
                aria-selected={isActive}
                type="button"
                onClick={() => setActiveFieldKey(f.key)}
                className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 ${
                  isActive
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Active sub-config editor */}
      <div className="flex-1 min-h-0 flex flex-col">
        {activeField.variant === "recipients" ? (
          <LeaveRecipientConfig field={activeField} />
        ) : activeField.variant === "uploadConfig" ? (
          <UploadConfigDetail key={activeField.key} field={activeField} embedded />
        ) : (
          <GenericConfigDetail key={activeField.key} field={activeField} embedded />
        )}
      </div>
    </div>
  );
};

export default ModuleConfig;
